import React, { Fragment, useState } from 'react'
import { Button, Tag, message, Tooltip, Progress } from 'antd'
import moment from 'moment'
import './ImageCard.scss'
import {
  DownloadOutlined,
  ReloadOutlined,
  CloseCircleFilled,
  CopyOutlined
} from '@ant-design/icons'
import JSZip from 'jszip'
import { ImagePreview } from '../../../components'
import { useTranslation } from 'react-i18next'

const ImageCard = ({
  data,
  imageIndex,
  isLoading,
  deleteImage,
  jobPercentage
}) => {
  const [isCopied, setIsCopied] = useState('')
  const [isDownloading, setIsDownloading] = useState(false)
  const [isDownloadingAll, setIsDownloadingAll] = useState(false)

  const handleDownloadAll = async () => {
    const zip = new JSZip()
    setIsDownloadingAll(true)
    const fetchImage = async (image, index) => {
      const urlParts = image.url.split('.')
      const format = urlParts[urlParts.length - 1]
      try {
        const response = await fetch(image.url)
        const blob = await response.blob()
        zip.file(`image${index + 1}.${format}`, blob)
      } catch (error) {
        console.error(`Error fetching ${image.url}:`, error)
      }
    }
    await Promise.all(
      data.images.map((image, index) => fetchImage(image, index))
    )

    const zipBlob = await zip.generateAsync({ type: 'blob' })

    const a = document.createElement('a')
    a.href = URL.createObjectURL(zipBlob)
    a.download = 'sharelook_images.zip'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(a.href)
    setIsDownloadingAll(false)
  }
  const formatDate = date => {
    const now = moment()
    const inputDate = moment(date)

    if (now.diff(inputDate, 'days') === 0) {
      return 'Today'
    } else if (now.diff(inputDate, 'days') === 1) {
      return 'Yesterday'
    } else if (now.startOf('week').isBefore(inputDate)) {
      return inputDate.format('dddd')
    } else {
      return inputDate.format('MMM D, YYYY')
    }
  }
  const download = async imageUrl => {
    setIsDownloading(true)
    try {
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = `${data.prompt}.${data.format}`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)

      window.URL.revokeObjectURL(url)
    } catch (error) {
      message.error('Error downloading the image:', error)
    } finally {
      setIsDownloading(false)
    }
  }
  const handleCopyToClipboard = seed => {
    navigator.clipboard
      .writeText(seed)
      .then(() => {
        setIsCopied(seed)
        setTimeout(() => setIsCopied(''), 1500)
      })
      .catch(err => console.error('Failed to copy text: ', err))
  }

  const { t } = useTranslation()

  return (
    <div className="image-result-container">
      <div className="prompt-section">
        <div>
          <p>
            <strong>{t('v4:prompt')} </strong>
            {data.prompt}
          </p>
        </div>
        <div>
          {!isLoading && !isDownloading && !isDownloadingAll && (
            <CloseCircleFilled
              onClick={() => {
                deleteImage(imageIndex)
              }}
              style={{
                fontSize: 18,
                color: 'red',
                margin: '5px'
              }}
            />
          )}
        </div>
      </div>

      <div className="images-grid">
        {isLoading && imageIndex === 0 ? (
          <Fragment>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 300,
                flexDirection: 'column',
                textAlign: 'center'
              }}
            >
              <Progress
                type="circle"
                percent={jobPercentage}
                strokeColor={{
                  '0%': '#1890ff',
                  '100%': '#52c41a'
                }}
                size={120}
              />
              <p
                style={{
                  marginTop: 20,
                  fontSize: 16,
                  fontWeight: 500,
                  color: '#555'
                }}
              >
                {t('v4:generated_image')}
              </p>
            </div>
          </Fragment>
        ) : (
          <Fragment>
            {data.images.map((image, i) => (
              <div key={i} className="image-card">
                <div className="image-wrapper">
                  <ImagePreview
                    src={image.url}
                    alt={`Generated image ${i + 1}`}
                    opacity={1}
                    crossorigin="anonymous"
                  />

                  {image.seed && (
                    <div
                      className="seed"
                      onClick={() => handleCopyToClipboard(image.seed)}
                      style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        cursor: 'pointer',
                        background:
                          isCopied === image.seed
                            ? '#5B6EE2'
                            : 'rgba(0,0,0,0.5)',
                        color: 'white',
                        padding: '5px',
                        borderRadius: '5px'
                      }}
                    >
                      <Tooltip title={'Copy Image Seed'}>
                        <CopyOutlined
                          style={{ fontSize: '16px', marginRight: '5px' }}
                        />
                      </Tooltip>
                      {isCopied === image.seed && 'Seed Copied!'}
                    </div>
                  )}
                  <div className="download-button">
                    {isDownloading ? (
                      <ReloadOutlined
                        spin
                        style={{
                          fontSize: 20,
                          color: '#5057d5',
                          margin: '5px',
                          cursor: 'pointer',
                          background: 'rgba(255, 255, 255, 0.5)',
                          borderRadius: '50%',
                          padding: 5
                        }}
                      />
                    ) : (
                      <DownloadOutlined
                        onClick={() => !isDownloadingAll && download(image.url)}
                        style={{
                          fontSize: 20,
                          color: '#5057d5',
                          borderRadius: '50%',
                          padding: 5,
                          margin: '5px',
                          cursor: 'pointer',
                          background: 'rgba(255, 255, 255, 0.5)'
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </Fragment>
        )}
      </div>

      <div className="metadata-section">
        <div className="tags-group">
          <Tag>{formatDate(data.created_at)}</Tag>
          <Tag>{data.aspect}</Tag>
          <Tag>{data.format}</Tag>
          <Tag>
            {Array.isArray(data.style)
              ? data.style.map(el => el).join(', ')
              : data.style}
          </Tag>
          <Tag>{data.performanceMode}</Tag>
        </div>
        <Button
          type="primary"
          className="download-all-button"
          onClick={handleDownloadAll}
          loading={isDownloadingAll}
          disabled={(isLoading && imageIndex === 0) || isDownloading}
        >
          {t('v4:download_add')}
        </Button>
      </div>
    </div>
  )
}

export default ImageCard
